import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordStrengthIndicator } from '@/components/ui/PasswordStrengthIndicator'
import { resetPassword } from '@/lib/api/auth'
import { useAuth } from '@/lib/auth-context'
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react'

// Password validation schema
const resetPasswordSchema = z.object({
  newPassword: z.string().min(4, 'Le mot de passe doit contenir au moins 4 caractères'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
})

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login: authLogin } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Extract token and email from URL
  const token = searchParams.get('token')
  const email = searchParams.get('email')

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  // Watch password for strength indicator
  const newPassword = watch('newPassword')

  // Validate token and email presence
  if (!token || !email) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-12">
            <h1 className="text-6xl font-bold text-primary mb-4">SOLY</h1>
            <div className="w-32 h-1.5 bg-primary mx-auto rounded-full mb-6"></div>
          </div>

          {/* Error card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>

            <h2 className="text-2xl font-bold text-center mb-4">
              Lien invalide
            </h2>

            <p className="text-center text-muted-foreground mb-6">
              Ce lien de réinitialisation est invalide ou mal formé.
              Veuillez demander un nouveau lien de réinitialisation.
            </p>

            <Link to="/forgot-password">
              <Button className="w-full h-12">
                Demander un nouveau lien
              </Button>
            </Link>

            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-sm text-primary hover:underline font-medium"
              >
                Retour à la connexion
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const onSubmit = async (data: ResetPasswordFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await resetPassword(token, email, data.newPassword)
      authLogin(response)
      navigate('/home')
    } catch (err: unknown) {
      const errData = (err instanceof Error && 'response' in err)
        ? (err as unknown as { response?: { data?: { Message?: string } } }).response?.data
        : undefined
      const errorMessage = errData?.Message

      if (errorMessage?.includes('token') || errorMessage?.includes('Token')) {
        setError('Le lien de réinitialisation a expiré ou est invalide. Veuillez en demander un nouveau.')
      } else if (errorMessage) {
        setError(errorMessage)
      } else {
        setError('Une erreur est survenue. Veuillez réessayer.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <h1 className="text-6xl font-bold text-primary mb-4">SOLY</h1>
          <div className="w-32 h-1.5 bg-primary mx-auto rounded-full mb-6"></div>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-center mb-2">
            Nouveau mot de passe
          </h2>
          <p className="text-center text-muted-foreground mb-8">
            Choisissez un mot de passe sécurisé pour <span className="font-medium text-foreground">{email}</span>
          </p>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <span className="text-red-600 text-lg">⚠️</span>
              <p className="text-sm text-red-600 flex-1">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* New password input */}
            <div className="space-y-3">
              <label htmlFor="newPassword" className="text-sm font-medium">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="🔒 Votre nouveau mot de passe"
                  autoComplete="new-password"
                  {...register('newPassword')}
                  autoFocus
                  data-testid="reset-password-input"
                  className={`pl-12 pr-12 h-14 text-base ${
                    errors.newPassword ? 'border-red-500 animate-shake' : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-sm text-red-600 flex items-center gap-1 animate-shake">
                  <span>⚠️</span>
                  <span>{errors.newPassword.message}</span>
                </p>
              )}

              {/* Password strength indicator */}
              <PasswordStrengthIndicator password={newPassword} />
            </div>

            {/* Confirm password input */}
            <div className="space-y-3">
              <label htmlFor="confirmPassword" className="text-sm font-medium">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirmez votre mot de passe"
                  autoComplete="new-password"
                  {...register('confirmPassword')}
                  data-testid="reset-confirm-input"
                  className={`pl-12 pr-12 h-14 text-base ${
                    errors.confirmPassword ? 'border-red-500 animate-shake' : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showConfirmPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-600 flex items-center gap-1 animate-shake">
                  <span>⚠️</span>
                  <span>{errors.confirmPassword.message}</span>
                </p>
              )}
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              data-testid="reset-submit-btn"
              className="w-full h-12 text-base font-semibold"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  Réinitialisation en cours...
                </>
              ) : (
                'Réinitialiser le mot de passe'
              )}
            </Button>
          </form>

          {/* Back to login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-primary hover:underline font-medium"
            >
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
